import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaBus, FaRoad, FaClock, FaRupeeSign, FaArrowRight, 
  FaPhoneAlt, FaWhatsapp, FaUsers, FaShieldAlt, 
  FaSnowflake, FaWifi, FaMapMarkedAlt, FaChair, 
  FaCrown, FaTv, FaMusic
} from 'react-icons/fa';
import { GiCarSeat, GiGearStick, GiSteeringWheel, GiCargoCrane, GiLuxuryCar } from 'react-icons/gi';
import { MdLocalGasStation, MdAirlineSeatRecline, MdLuggage, MdAcUnit } from 'react-icons/md';
import { BiWind } from 'react-icons/bi';
import { buildLandingBookingState, loadLandingPageVehicles } from './vehicleLandingPageData';

const urbaniaImageMap = {
  Urbania: 'https://forceurbania.co.in/wp-content/uploads/2022/12/STYLE-1.jpg',
  default: 'https://www.ecorentacar.com/wp-content/uploads/2024/09/Force-Urbania-01.jpg',
};

const UrbaniaPage = () => {
  // Tab state: 'local' or 'outstation'
  const [activeTab, setActiveTab] = useState('local');
  const [apiUrbaniaList, setApiUrbaniaList] = useState({ outstation: [], local: [] });
  const [isLoadingApiVehicles, setIsLoadingApiVehicles] = useState(true);
  const navigate = useNavigate();

  // Local Urbania Data
  const urbaniaLocal = [
    {
      id: 'urbania9',
      name: 'Urbania Luxury',
      variant: '9 Seater (1+1)',
      seats: '9',
      price: 7500,
      baseKm: 80,
      baseHours: 8,
      extraKm: 40,
      extraHour: 550,
      fuel: 'Diesel',
      transmission: 'Manual',
      ac: 'Premium Climate Control',
      luggage: 'Large Boot',
      features: 'Pushback seats, WiFi, USB charging',
      image: 'https://images.jdmagicbox.com/quickquotes/images_main/urbania-3350wb-9-seater-van-diesel-272860979-r7czp.jpg'
    },
    {
      id: 'urbania1012',
      name: 'Urbania Luxury',
      variant: '10/12 Seater (1+1)',
      seats: '10 or 12',
      price: 8500,
      baseKm: 80,
      baseHours: 8,
      extraKm: 50,
      extraHour: 600,
      fuel: 'Diesel',
      transmission: 'Manual',
      ac: 'Premium Climate Control',
      luggage: 'Extra Large Boot',
      features: 'Luxury pushback seats, ambient lighting, entertainment system',
      image: 'https://forceurbania.co.in/wp-content/uploads/2022/12/STYLE-1.jpg'
    },
    {
      id: 'urbania16',
      name: 'Urbania Luxury',
      variant: '16 Seater',
      seats: '16',
      price: 8500,
      baseKm: 80,
      baseHours: 8,
      extraKm: 45,
      extraHour: 550,
      fuel: 'Diesel',
      transmission: 'Manual',
      ac: 'Premium 3-zone AC',
      luggage: 'Massive Boot Space',
      features: 'Luxury interiors, ample legroom, premium sound system',
      image: 'https://stimg.cardekho.com/images/carexteriorimages/930x620/Force/Urbania/11849/1763466348855/front-left-side-47.jpg'
    }
  ];

  // Outstation Urbania Data
  const urbaniaOutstation = [
    {
      id: 'urbania16-out',
      name: 'Urbania Luxury',
      variant: '16+1 Seater',
      seats: '16+1',
      pricePerKm: 38,
      minKms: 300,
      driverBeta: 800,
      fuel: 'Diesel',
      transmission: 'Manual',
      ac: 'Premium 3-zone AC',
      features: 'Luxury pushback seats, entertainment system',
      image: 'https://www.ecorentacar.com/wp-content/uploads/2024/09/Force-Urbania-01.jpg'
    },
    {
      id: 'urbania12-out',
      name: 'Urbania Luxury',
      variant: '12+1 Seater',
      seats: '12+1',
      pricePerKm: 48,
      minKms: 300,
      driverBeta: 800,
      fuel: 'Diesel',
      transmission: 'Manual',
      ac: 'Premium Climate Control',
      features: 'Extra legroom, premium interiors',
      image: 'https://www.transpireholidays.biz/wp-content/uploads/2025/10/Premium-Force-Urbania-Kochi-Cochin.png'
    },
    {
      id: 'urbania9-out',
      name: 'Urbania Luxury',
      variant: '9+1 Seater Luxury',
      seats: '9+1',
      pricePerKm: 60,
      minKms: 300,
      driverBeta: 800,
      fuel: 'Diesel',
      transmission: 'Manual',
      ac: 'Premium Climate Control',
      features: 'Ultra-luxury pushback seats',
      image: 'https://images.jdmagicbox.com/quickquotes/images_main/urbania-3350wb-9-d-seater-van-diesel-grey-272995603-5ly03.png'
    },
    {
      id: 'urbania9pkn-out',
      name: 'Urbania PKN',
      variant: '9+1 PKN Luxury',
      seats: '9+1',
      pricePerKm: 65,
      minKms: 300,
      driverBeta: 800,
      fuel: 'Diesel',
      transmission: 'Manual',
      ac: 'Premium PKN Edition',
      features: 'PKN luxury package, massage chairs, premium audio',
      isPremium: true,
      image: 'https://www.transpireholidays.biz/wp-content/uploads/2025/10/Premium-Force-Urbania-Kochi-Cochin.png'
    }
  ];

  useEffect(() => {
    let isMounted = true;

    const loadVehicles = async () => {
      try {
        setIsLoadingApiVehicles(true);
        const nextVehicles = await loadLandingPageVehicles('urbania', urbaniaImageMap);
        if (isMounted) setApiUrbaniaList(nextVehicles);
      } catch (error) {
        if (isMounted) setApiUrbaniaList({ outstation: [], local: [] });
      } finally {
        if (isMounted) setIsLoadingApiVehicles(false);
      }
    };

    loadVehicles();

    return () => {
      isMounted = false;
    };
  }, []);

  const currentData = apiUrbaniaList[activeTab]?.length
    ? apiUrbaniaList[activeTab]
    : activeTab === 'local'
      ? urbaniaLocal
      : urbaniaOutstation;

  const handleBookNow = (urbania) => {
    const apiBookingState = buildLandingBookingState(urbania, activeTab);
    if (apiBookingState) {
      navigate('/booking', { state: apiBookingState });
      return;
    }

    const isLocal = activeTab === 'local';
    const vehicleData = {
      id: urbania.id,
      name: `${urbania.name} ${urbania.variant}`,
      variant: urbania.variant,
      type: 'Urbania',
      category: 'Urbania',
      seats: urbania.seats,
      fuel: urbania.fuel,
      transmission: urbania.transmission,
      ac: urbania.ac,
      luggage: urbania.luggage,
      image: urbania.image,
      features: urbania.features,
      isPremium: urbania.isPremium,
      price: isLocal ? urbania.price : urbania.pricePerKm,
      baseKm: urbania.baseKm,
      baseHours: urbania.baseHours,
      extraKm: urbania.extraKm,
      extraHour: urbania.extraHour,
      pricePerKm: urbania.pricePerKm,
      driverBeta: urbania.driverBeta,
      minKms: urbania.minKms,
    };
    const bookingData = {
      vehicle: vehicleData,
      route: {
        routeName: `${urbania.name} ${urbania.variant} ${isLocal ? 'local package' : 'outstation package'}`,
        pickup: '',
        dropoff: '',
        distance: isLocal ? urbania.baseKm : urbania.minKms,
        estimatedTime: isLocal ? `${urbania.baseHours} hours` : '',
        price: isLocal ? urbania.price : urbania.pricePerKm,
        tripDirection: isLocal ? 'Local' : 'Outstation',
        tripType: activeTab,
      },
      pricing: isLocal
        ? {
            basePrice: urbania.price,
            includedKms: urbania.baseKm,
            includedHours: urbania.baseHours,
            extraPerKm: urbania.extraKm,
            extraPerHour: urbania.extraHour,
          }
        : {
            basePrice: urbania.pricePerKm,
            pricePerKm: urbania.pricePerKm,
            driverBata: urbania.driverBeta,
            minKms: urbania.minKms,
          },
    };

    navigate('/booking', {
      state: {
        selectedVehicleData: vehicleData,
        initialRouteData: bookingData,
        tripType: isLocal ? 'Local Taxi' : 'Outstation',
      },
    });
  };

  return (
    <div className="min-h-screen bg-white text-black font-sans">
      {/* Hero Section with Urbania Theme */}
      <div className="relative bg-gradient-to-r from-white via-white to-yellow-50 border-b-4 border-yellow-500">
        <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
          <div className="text-center md:text-left md:flex md:justify-between md:items-end">
            <div>
              <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-800 px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
                <FaCrown className="text-yellow-600" />
                <span>Ultra Luxury Fleet</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">
                Urbania
                <br />
                <span className="text-yellow-600">Luxury Travel</span>
              </h1>
              <p className="text-gray-600 mt-3 max-w-lg">
                Experience premium group travel with Urbania luxury vans. 
                Spacious pushback seats, premium entertainment, and professional chauffeurs.
              </p>
            </div>
            {/* <div className="flex gap-3 mt-6 md:mt-0">
              <button className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-xl font-semibold transition shadow-md">
                <FaWhatsapp size={18} />
                <span>WhatsApp</span>
              </button>
              <button className="flex items-center gap-2 border-2 border-yellow-500 text-yellow-700 hover:bg-yellow-50 px-5 py-2.5 rounded-xl font-semibold transition">
                <FaPhoneAlt size={14} />
                <span>Call Now</span>
              </button>
            </div> */}
          </div>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="max-w-7xl mx-auto px-4 mt-6 mb-2">
        <div className="flex flex-wrap gap-3 border-b border-gray-200 pb-2">
          <button
            onClick={() => setActiveTab('local')}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-t-xl font-bold text-base transition-all ${
              activeTab === 'local'
                ? 'bg-yellow-500 text-white shadow-md -mb-[2px] border-b-2 border-yellow-600'
                : 'bg-gray-100 text-gray-700 hover:bg-yellow-100'
            }`}
          >
            <FaClock className="text-sm" />
            LOCAL / ROUND TRIP
          </button>
          <button
            onClick={() => setActiveTab('outstation')}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-t-xl font-bold text-base transition-all ${
              activeTab === 'outstation'
                ? 'bg-yellow-500 text-white shadow-md -mb-[2px] border-b-2 border-yellow-600'
                : 'bg-gray-100 text-gray-700 hover:bg-yellow-100'
            }`}
          >
            <FaMapMarkedAlt className="text-sm" />
            OUTSTATION / ONE WAY
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
          <span className="inline-block w-1.5 h-1.5 bg-yellow-500 rounded-full"></span>
          {activeTab === 'local'
            ? 'Local packages: 8h/80km included. Toll and parking extra as per actuals.'
            : 'Outstation: 300km/day included • Perfect for luxury group tours and corporate travel'}
        </p>
      </div>

      {/* Cards Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {isLoadingApiVehicles && (
          <div className="mb-4 rounded-xl bg-yellow-50 px-4 py-3 text-sm font-semibold text-yellow-800">
            Loading latest Urbania rates...
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {currentData.map((urbania) => (
            <div
              key={urbania.id}
              className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 flex flex-col"
            >
              {/* Premium Badge */}
              <div className="relative h-52 bg-gradient-to-br from-gray-50 to-yellow-50 overflow-hidden">
                <img
                  src={urbania.image}
                  alt={urbania.name}
                  className="w-full h-full object-cover transition duration-500 hover:scale-105"
                  onError={(e) => {
                    e.currentTarget.src = 'https://imgd.aeplcdn.com/1056x594/n/cw/ec/123405/force-traveller-exterior-right-front-three-quarter-3.jpeg?isig=0&q=80';
                  }}
                />
                <div className="absolute top-3 left-3 bg-black/70 backdrop-blur-sm text-white text-xs font-bold px-2 py-1 rounded-lg flex items-center gap-1">
                  <GiCarSeat size={12} />
                  <span>{urbania.seats} Seats</span>
                </div>
                <div className="absolute top-3 right-3 bg-yellow-500 text-black font-extrabold text-xs px-2.5 py-1 rounded-full shadow-md flex items-center gap-1">
                  <FaCrown size={10} />
                  <span>Luxury</span>
                </div>
                {(urbania.isPremium || urbania.id === 'urbania9pkn-out') && (
                  <div className="absolute bottom-3 left-3 bg-gradient-to-r from-yellow-600 to-yellow-500 text-black text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow-lg">
                    <FaCrown size={10} />
                    <span>PKN Premium</span>
                  </div>
                )}
                {urbania.features && (
                  <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-sm text-yellow-300 text-xs font-bold px-2 py-1 rounded-lg flex items-center gap-1">
                    <FaTv size={10} />
                    <span>Entertainment</span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-5 flex-1 flex flex-col">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="text-xl font-extrabold text-gray-900 tracking-tight">{urbania.name}</h3>
                    <p className="text-sm text-yellow-600 font-semibold">{urbania.variant}</p>
                  </div>
                  <div className="bg-gradient-to-br from-yellow-400 to-yellow-500 p-2 rounded-full shadow-md">
                    <MdLocalGasStation className="text-white text-lg" />
                  </div>
                </div>

                {/* Specs row */}
                <div className="flex flex-wrap gap-2 text-xs text-gray-600 mb-4 border-b border-gray-100 pb-3">
                  <div className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-md">
                    <MdLocalGasStation className="text-yellow-600" />
                    <span>{urbania.fuel}</span>
                  </div>
                  <div className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-md">
                    <GiGearStick className="text-yellow-600" />
                    <span>{urbania.transmission}</span>
                  </div>
                  <div className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-md">
                    <MdAcUnit className="text-yellow-600" />
                    <span>{urbania.ac}</span>
                  </div>
                  <div className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-md">
                    <MdLuggage className="text-yellow-600" />
                    <span>Luggage</span>
                  </div>
                </div>

                {/* PRICE Section */}
                <div className="mb-4">
                  {activeTab === 'local' ? (
                    <>
                      <div className="flex items-baseline gap-1 flex-wrap">
                        <span className="text-3xl font-black text-yellow-600">
                          <FaRupeeSign className="inline text-2xl mr-0.5" />
                          {urbania.price.toLocaleString()}
                        </span>
                        <span className="text-gray-500 text-sm font-medium">/ day</span>
                      </div>
                      <div className="flex items-center gap-2 mt-2 text-sm font-semibold text-gray-700 bg-yellow-50 inline-flex px-3 py-1 rounded-full">
                        <FaClock className="text-yellow-600" />
                        <span>{urbania.baseHours}h | {urbania.baseKm}km included</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-1 flex-wrap">
                        <span className="text-3xl font-black text-yellow-600">
                          <FaRupeeSign className="inline text-2xl mr-0.5" />
                          {urbania.pricePerKm}
                        </span>
                        <span className="text-gray-500 text-sm font-medium">/ km</span>
                      </div>
                      <div className="flex items-center gap-2 mt-2 text-sm font-semibold text-gray-700 bg-yellow-50 inline-flex px-3 py-1 rounded-full">
                        <FaRoad className="text-yellow-600" />
                        <span>Min {urbania.minKms} km/day</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Extra Details based on tab */}
                {activeTab === 'local' ? (
                  <div className="space-y-2 text-sm bg-gray-50 p-3 rounded-xl mb-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 flex items-center gap-1">
                        <FaRoad className="text-yellow-500 text-xs" /> Extra km:
                      </span>
                      <span className="font-bold text-black">
                        <FaRupeeSign className="inline text-xs" /> {urbania.extraKm}/km
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 flex items-center gap-1">
                        <FaClock className="text-yellow-500 text-xs" /> Extra hour:
                      </span>
                      <span className="font-bold text-black">
                        <FaRupeeSign className="inline text-xs" /> {urbania.extraHour}/hr
                      </span>
                    </div>
                    <div className="text-xs text-red-600 bg-red-50 p-2 rounded-md mt-1 flex items-start gap-1">
                      <span className="font-bold">Note:</span> Toll and parking extra as per actuals.
                    </div>
                    {urbania.features && (
                      <div className="text-xs text-yellow-700 bg-yellow-50 p-2 rounded-md mt-1 flex items-center gap-1 flex-wrap">
                        <FaWifi size={10} />
                        <span>{urbania.features}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2 text-sm bg-gray-50 p-3 rounded-xl mb-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 flex items-center gap-1">
                        <FaRoad className="text-yellow-500 text-xs" /> Rate per km:
                      </span>
                      <span className="font-bold text-black">
                        <FaRupeeSign className="inline text-xs" /> {urbania.pricePerKm}/km
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 flex items-center gap-1">
                        <FaMapMarkedAlt className="text-yellow-500 text-xs" /> Min. km/day:
                      </span>
                      <span className="font-bold">{urbania.minKms} km</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 flex items-center gap-1">
                        <GiSteeringWheel className="text-yellow-500 text-xs" /> Driver Bata:
                      </span>
                      <span className="font-bold">
                        <FaRupeeSign className="inline text-xs" /> {urbania.driverBeta}/day
                      </span>
                    </div>
                    <div className="text-xs text-red-600 bg-red-50 p-2 rounded-md mt-1 flex items-start gap-1">
                      <span className="font-bold">⚠️</span> Toll, parking & state permit extra (actual charges)
                    </div>
                  </div>
                )}

                {/* CTA Button */}
                <button
                  onClick={() => handleBookNow(urbania)}
                  className="mt-2 w-full bg-yellow-500 hover:bg-yellow-600 text-black font-extrabold py-3 rounded-xl flex items-center justify-center gap-2 transition-all duration-200 shadow-md hover:shadow-lg group"
                >
                  <span>Book {urbania.variant}</span>
                  <FaArrowRight className="group-hover:translate-x-1 transition-transform text-sm" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Why Choose Urbania Section */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-gradient-to-r from-yellow-50 to-white rounded-2xl p-6 border border-yellow-100">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FaCrown className="text-yellow-600" />
            Why Choose Urbania Luxury Fleet?
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2 text-sm">
              <FaChair className="text-yellow-600" />
              <span>Pushback Seats</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <FaSnowflake className="text-yellow-600" />
              <span>Premium AC</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <FaTv className="text-yellow-600" />
              <span>Entertainment System</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <FaMusic className="text-yellow-600" />
              <span>Premium Sound</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <FaWifi className="text-yellow-600" />
              <span>Complimentary WiFi</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <FaShieldAlt className="text-yellow-600" />
              <span>Insurance Covered</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <FaUsers className="text-yellow-600" />
              <span>Professional Drivers</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <MdLuggage className="text-yellow-600" />
              <span>Ample Luggage Space</span>
            </div>
          </div>
        </div>
      </div>

      {/* Important Notes Disclaimer */}
      <div className="max-w-7xl mx-auto px-4 pb-4">
        <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-xl p-4 text-sm text-gray-700">
          <p className="font-bold text-black mb-2 flex items-center gap-2">
            <FaCrown className="text-yellow-600" />
            Important Information - Urbania Luxury:
          </p>
          <ul className="space-y-1 text-gray-700 ml-4">
            <li>• <span className="font-semibold">Local Package:</span> 8 hours / 80 km included. Extra km, extra hour, toll, and parking charges are extra.</li>
            <li>• <span className="font-semibold">Outstation Package:</span> 300 km per day included. Driver bata: ₹800/day.</li>
            <li>• Toll, parking, interstate permit, and night charges (if applicable) are extra as per actuals.</li>
            <li>• All Urbania vans come with luxury pushback seats, premium AC, and entertainment system.</li>
            <li>• <span className="font-semibold">PKN variant:</span> Premium package with massage chairs and enhanced audio system.</li>
            <li>• Ideal for corporate events, wedding guest transport, airport transfers, and luxury group tours.</li>
          </ul>
        </div>
      </div>

      {/* Footer */}
      {/* <footer className="bg-gray-900 text-white mt-8 py-6 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex gap-4 items-center">
            <div className="bg-yellow-500 p-2 rounded-full">
              <FaPhoneAlt className="text-black" />
            </div>
            <div>
              <p className="text-sm text-gray-300">24/7 Luxury Travel Support</p>
              <p className="font-bold text-lg">+91 98765 43210</p>
            </div>
          </div>
          <div className="text-center text-gray-400 text-sm">
            <p>© 2025 Urbania Luxury Travel | Premium Group Transport | Clean & Sanitized Fleet</p>
          </div>
          <div className="flex gap-3">
            <FaWhatsapp className="text-green-400 text-xl cursor-pointer hover:scale-110 transition" />
            <FaCrown className="text-yellow-400 text-xl cursor-pointer hover:scale-110 transition" />
          </div>
        </div>
      </footer> */}
    </div>
  );
};

export default UrbaniaPage;
